/**
 * Shared configuration for outbound integrations (WhatsApp, SMS, RCS, Email, Voice).
 * Single source of truth for integration display names, config keys, and recipient types.
 * Used by NewMessageModal and campaigns features.
 */

import { IntegrationType } from '@/shared/types/integration';

// Metadata lookup for all outbound integration types (including Voice).
// Used by helpers below for display names, config keys, recipient types, etc.
export const OUTBOUND_INTEGRATIONS = {
  [IntegrationType.WHATSAPP]: {
    displayName: 'WhatsApp',
    configKey: 'display_phone_number',
    recipientType: 'phone' as const,
    requiresTemplate: true,
    requiresContent: false,
  },
  [IntegrationType.INFOBIP_SMS]: {
    displayName: 'SMS',
    configKey: 'number',
    recipientType: 'phone' as const,
    requiresTemplate: false,
    requiresContent: true,
  },
  [IntegrationType.INFOBIP_RCS]: {
    displayName: 'RCS',
    configKey: 'sender_name',
    recipientType: 'phone' as const,
    requiresTemplate: false,
    requiresContent: true,
  },
  [IntegrationType.EMAIL]: {
    displayName: 'Email',
    configKey: 'from_email',
    recipientType: 'email' as const,
    requiresTemplate: false,
    requiresContent: true,
  },
  [IntegrationType.TWILIO_VOICE]: {
    displayName: 'Voice',
    configKey: 'phone_number',
    recipientType: 'phone' as const,
    requiresTemplate: false,
    requiresContent: true,
  },
  [IntegrationType.TELNYX_VOICE]: {
    displayName: 'Voice',
    configKey: 'phone_number',
    recipientType: 'phone' as const,
    requiresTemplate: false,
    requiresContent: true,
  },
  [IntegrationType.AGORA_VOICE]: {
    displayName: 'Voice',
    configKey: 'phone_number',
    recipientType: 'phone' as const,
    requiresTemplate: false,
    requiresContent: true,
  },
} as const;

// Types
export type OutboundIntegrationType = keyof typeof OUTBOUND_INTEGRATIONS;
export type RecipientType = 'phone' | 'email';

// Flat list of integration types that support outbound messaging (excludes Voice).
// Used for NewMessageModal integration filtering and "New Message" button visibility.
export const DIRECT_MESSAGE_INTEGRATION_TYPES: IntegrationType[] = (
  Object.keys(OUTBOUND_INTEGRATIONS) as OutboundIntegrationType[]
).filter(
  (type) =>
    type !== IntegrationType.TWILIO_VOICE &&
    type !== IntegrationType.TELNYX_VOICE &&
    type !== IntegrationType.AGORA_VOICE
);

// Helpers
export const getDisplayName = (type: IntegrationType): string =>
  OUTBOUND_INTEGRATIONS[type as OutboundIntegrationType]?.displayName ?? type;

export const getConfigKey = (type: IntegrationType): string | undefined =>
  OUTBOUND_INTEGRATIONS[type as OutboundIntegrationType]?.configKey;

export const getRecipientType = (type: IntegrationType): RecipientType | null =>
  OUTBOUND_INTEGRATIONS[type as OutboundIntegrationType]?.recipientType ?? null;

export const requiresTemplate = (type: IntegrationType): boolean =>
  OUTBOUND_INTEGRATIONS[type as OutboundIntegrationType]?.requiresTemplate ?? false;

export const requiresContent = (type: IntegrationType): boolean =>
  OUTBOUND_INTEGRATIONS[type as OutboundIntegrationType]?.requiresContent ?? false;
